<template>
  <div class="pdf-field-editor" :class="'pdf-field-editor--' + party">
    <!-- Loading state (PDF not yet rendered) -->
    <div v-if="!pdfRendered" class="pdf-loading-state">
      <i class="fa fa-spinner fa-spin"></i>
      <p>Loading PDF…</p>
    </div>

    <!-- PDF Viewer + Field Placement -->
    <!-- v-show keeps canvases in the DOM during painting; visibility hidden until pdfRendered -->
    <div
      v-show="pdfLoaded"
      class="pdf-viewer-container"
      :class="{ 'pdf-viewer--painting': pdfLoaded && !pdfRendered }"
    >

      <!-- Active field type hint -->
      <div v-if="selectedFieldType" class="pdf-place-hint">
        <i class="fa fa-crosshairs"></i>
        Click anywhere on the PDF to place a <strong>{{ fieldTypeLabel(selectedFieldType) }}</strong> field
      </div>

      <!-- Pages -->
      <div
        v-for="(page, idx) in renderedPages"
        :key="idx"
        class="pdf-page-wrap"
        :style="{ width: page.width + 'px', height: page.height + 'px' }"
      >
        <canvas
          :ref="'canvas_' + idx"
          :width="page.width"
          :height="page.height"
          class="pdf-page-canvas"
        ></canvas>
        <!-- Click overlay (hidden in sign/view modes) -->
        <div
          v-if="mode !== 'sign' && mode !== 'view'"
          class="pdf-page-overlay"
          :class="{ 'pdf-page-overlay--active': !!selectedFieldType }"
          :style="{ width: page.width + 'px', height: page.height + 'px' }"
          @click="onPageClick($event, idx)"
        ></div>
        <!-- Fields on this page -->
        <template v-for="field in fieldsOnPage(idx)">
          <div
            :key="field.id"
            :data-field-id="field.id"
            class="pdf-field"
            :class="[
              'pdf-field--' + field.type,
              'pdf-field--party-' + (field.executing_party || party),
              {
                'pdf-field--sign-mode': mode === 'sign',
                'pdf-field--view-mode': mode === 'view',
                'pdf-field--dragging': dragState && dragState.id === field.id,
                'pdf-field--selected': selectedFieldId === field.id && mode !== 'sign' && mode !== 'view',
              }
            ]"
            :style="fieldStyle(field, page)"
            @mousedown.stop="onFieldMouseDown($event, field, idx)"
            @click.stop="onFieldClick(field)"
            @dblclick.stop="onFieldDblClick(field)"
          >
            <template v-if="mode === 'view'">
              <span class="pdf-field-label">{{ fieldLabel(field) }}</span>
            </template>
            <template v-else-if="mode === 'sign' && isSignableField(field)">
              <template v-if="field._signedDataUrl">
                <img
                  :src="field._signedDataUrl"
                  class="pdf-field-signed-img"
                  :class="{ 'pdf-field-signed-img--resigable': field.type === 'initials' }"
                  @click.stop="field.type === 'initials' ? $emit('sign-field', { field, event: $event }) : undefined"
                  :title="field.type === 'initials' ? 'Click to change initial' : ''"
                />
                <button
                  class="pdf-field-clear-sign"
                  @click.stop="$emit('clear-sign', field)"
                  title="Remove signature"
                ><i class="fa fa-times"></i></button>
              </template>
              <button v-else class="pdf-field-sign-btn" @click.stop="$emit('sign-field', { field, event: $event })">
                <i :class="field.type === 'initials' ? 'fa fa-font' : 'fa fa-pencil'"></i>
                {{ field.type === 'initials' ? 'Click to initial' : 'Click to sign' }}
              </button>
            </template>
            <template v-else-if="mode === 'sign' && isFillableField(field) && field.type === 'date'">
              <span class="pdf-field-filled-value pdf-field-filled-value--readonly">{{ field._filledValue || '' }}</span>
            </template>
            <template v-else-if="mode === 'sign' && isFillableField(field)">
              <button class="pdf-field-fill-btn" @click.stop="$emit('fill-field', { field, event: $event })">
                <span v-if="field._filledValue" class="pdf-field-filled-value">{{ field._filledValue }}</span>
                <template v-else>
                  <span class="pdf-field-fill-placeholder">{{ fieldLabel(field) }}</span>
                  <span class="pdf-field-fill-hint"><i class="fa fa-keyboard-o"></i> Click to fill</span>
                </template>
              </button>
            </template>
            <template v-else>
              <template v-if="editingLabelId === field.id">
                <input
                  class="pdf-field-label-input"
                  v-model="editingLabelValue"
                  @blur="saveLabelEdit(field)"
                  @keydown.enter.stop="saveLabelEdit(field)"
                  @keydown.esc.stop="cancelLabelEdit()"
                  @click.stop
                  @mousedown.stop
                  :ref="'labelInput_' + field.id"
                  maxlength="60"
                />
              </template>
              <template v-else>
                <span class="pdf-field-label">{{ fieldLabel(field) }}</span>
                <span v-if="mode !== 'sign'" class="pdf-field-actions">
                  <button
                    class="pdf-field-action-btn pdf-field-remove"
                    @click.stop="removeField(field.id)"
                    title="Remove field"
                  ><i class="fa fa-trash"></i></button>
                  <span class="pdf-field-action-btn pdf-field-drag-handle" title="Drag to move">
                    <i class="fa fa-arrows"></i>
                  </span>
                </span>
              </template>
              <!-- Resize handles: all field types, own party, when selected -->
              <template v-if="field.executing_party === party && selectedFieldId === field.id">
                <span class="pdf-field-resize-handle pdf-field-resize-handle--tl" @mousedown.stop="onResizeMouseDown($event, field, idx, 'tl')" title="Drag to resize"></span>
                <span class="pdf-field-resize-handle pdf-field-resize-handle--tr" @mousedown.stop="onResizeMouseDown($event, field, idx, 'tr')" title="Drag to resize"></span>
                <span class="pdf-field-resize-handle pdf-field-resize-handle--bl" @mousedown.stop="onResizeMouseDown($event, field, idx, 'bl')" title="Drag to resize"></span>
                <span class="pdf-field-resize-handle pdf-field-resize-handle--br" @mousedown.stop="onResizeMouseDown($event, field, idx, 'br')" title="Drag to resize"></span>
              </template>
            </template>
          </div>
        </template>

        <!-- Type switcher popover next to the selected field (not for initials) -->
        <div
          v-if="selectedFieldOnPage(idx) && mode !== 'sign' && mode !== 'view' && selectedFieldOnPage(idx).type !== 'initials'"
          class="pdf-type-switcher"
          :style="typeSwitcherStyle(selectedFieldOnPage(idx), page)"
          @mousedown.stop
          @click.stop
        >
          <button
            v-for="(opt, i) in switcherOptions"
            :key="opt.type"
            class="pdf-type-switcher-item"
            :class="{
              'pdf-type-switcher-item--active': selectedFieldOnPage(idx).type === opt.type,
              'pdf-type-switcher-item--used':   isTypeAlreadyUsed(opt.type) && selectedFieldOnPage(idx).type !== opt.type,
            }"
            :disabled="isTypeAlreadyUsed(opt.type) && selectedFieldOnPage(idx).type !== opt.type"
            @click="changeSelectedFieldType(opt.type)"
          >
            <span class="pdf-type-switcher-icon" v-html="typeIconSvg(opt.type)"></span>
            <span class="pdf-type-switcher-label">{{ opt.label }}</span>
            <span v-if="i < switcherOptions.length - 1" class="pdf-type-switcher-sep"></span>
          </button>
        </div>

        <div class="pdf-page-number">Page {{ idx + 1 }}</div>
      </div>
    </div>
  </div>
</template>

<script>
const FIELD_TYPES = {
  venue: [
    { type: 'signature', label: 'Signature' },
    { type: 'date', label: 'Date' },
    { type: 'text', label: 'Text' },
  ],
  client: [
    { type: 'signature', label: 'Signature' },
    { type: 'date', label: 'Date' },
    { type: 'text', label: 'Text' },
  ],
};

// These types may only appear once per party within a single editor step
const UNIQUE_FIELD_TYPES = new Set(['signature', 'date']);

const FIELD_DIMS = {
  signature:    { width: 0.25, height: 0.04 },
  initials:     { width: 0.18, height: 0.055 },
  date:         { width: 0.20, height: 0.045 },
  text:         { width: 0.25, height: 0.045 },
};

// Minimum dimensions per field type (used during resize)
const FIELD_MIN_DIMS = {
  signature:    { width: 0.08, height: 0.025 },
  initials:     { width: 0.08, height: 0.025 },
  date:         { width: 0.12, height: 0.035 },
  text:         { width: 0.12, height: 0.035 },
};

let fieldCounter = 0;

export default {
  name: 'PdfFieldEditor',
  props: {
    party: {
      type: String,
      required: true, // 'venue' or 'client'
    },
    // existing fields from a previous step (read-only display)
    existingFields: {
      type: Array,
      default: () => [],
    },
    // preloaded PDF bytes (Uint8Array) when editing an existing contract
    initialPdfBytes: {
      default: null,
    },
    // pre-selected File object (from UploadContractModal)
    initialFile: {
      type: File,
      default: null,
    },
    // active field type controlled by parent (null = no placement mode)
    selectedFieldType: {
      type: String,
      default: null,
    },
    // 'place' (default) — interactive field placement
    // 'sign' — read-only overlay; venue signature fields show "Click to sign"
    // 'view' — fully read-only; renders fields as static labeled placeholders
    mode: {
      type: String,
      default: 'place',
    },
    // parties whose signature/initials fields are interactive in sign mode
    signableParties: {
      type: Array,
      default: () => ['venue'],
    },
  },
  data() {
    return {
      pdfLoaded: false,
      pdfRendered: false,
      pdfFile: null,
      pdfBytes: null,
      pdfDoc: null,
      renderedPages: [],
      fields: [],
      isDragOver: false,
      dragState: null,
      resizeState: null,
      selectedFieldId: null,
      editingLabelId: null,
      editingLabelValue: '',
      _renderTasks: [],
      _loadPromise: null,
    };
  },
  computed: {
    switcherOptions() {
      return [
        { type: 'signature', label: 'Signature Field' },
        { type: 'date',      label: 'Date Field' },
        { type: 'text',      label: 'Text Field' },
      ];
    },
  },
  watch: {
    initialPdfBytes: {
      immediate: true,
      async handler(bytes) {
        if (bytes) {
          await this.loadPdfBytes(bytes);
        }
      },
    },
    initialFile: {
      immediate: true,
      async handler(file) {
        if (file) {
          await this.loadFile(file);
        }
      },
    },
    existingFields(newFields) {
      // Preserve own fields already placed; replace only the cross-party (read-only) ones.
      const ownFields = this.fields.filter(f => f.executing_party === this.party);
      this.fields = [...(newFields || []).map(f => ({ ...f })), ...ownFields];
    },
  },
  mounted() {
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
    // Pre-populate fields from existingFields (e.g. venue fields shown in client step)
    this.fields = (this.existingFields || []).map(f => ({ ...f }));
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  },
  methods: {
    fieldTypeLabel(type) {
      const labels = {
        signature: 'Signature', initials: 'Initials', date: 'Date', text: 'Text',
      };
      return labels[type] || type;
    },

    async loadFile(file) {
      if (file.size > 20 * 1024 * 1024) {
        this.$message && this.$message.error('PDF must be under 20MB');
        return;
      }
      this.pdfFile = file;
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      await this.loadPdfBytes(bytes);
    },

    async loadPdfBytes(bytes) {
      // Serialize concurrent calls (the immediate watcher can fire repeatedly when
      // the parent re-passes bytes) so render() never runs twice on the same canvas.
      const run = async () => {
        // Hide content while re-rendering to avoid flicker on PDF reload.
        this.pdfRendered = false;
        // Cancel any in-flight page renders before tearing down the old document.
        this._cancelRenderTasks();
        this.pdfBytes = bytes;
        const pdfjsLib = await this.getPdfjsLib();
        // pdfjs transfers (detaches) the ArrayBuffer to the worker thread, so
        // pass a copy so the original stays intact for storage and re-use.
        this.pdfDoc = await pdfjsLib.getDocument({ data: bytes.slice(0) }).promise;
        await this.collectPageSizes();
        // Set pdfLoaded BEFORE rendering so canvases are mounted in the DOM first
        this.pdfLoaded = true;
        this.$emit('pdf-loaded', { file: this.pdfFile, bytes });
        await this.$nextTick();
        await this.paintCanvases();
        // Only reveal canvases after they are fully painted to avoid blank-canvas flicker.
        this.pdfRendered = true;
      };
      const prev = this._loadPromise || Promise.resolve();
      this._loadPromise = prev.catch(() => {}).then(run);
      return this._loadPromise;
    },

    _cancelRenderTasks() {
      if (!this._renderTasks) return;
      for (const task of this._renderTasks) {
        try { task.cancel(); } catch (_) { /* ignore */ }
      }
      this._renderTasks = [];
    },

    async getPdfjsLib() {
      if (window.pdfjsLib) return window.pdfjsLib;
      if (!window.__pdfjsLoadPromise) {
        window.__pdfjsLoadPromise = new Promise((resolve, reject) => {
          const existing = document.querySelector('script[data-pdfjs]');
          if (existing) {
            existing.addEventListener('load', resolve);
            existing.addEventListener('error', reject);
            return;
          }
          const script = document.createElement('script');
          script.setAttribute('data-pdfjs', '1');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        }).then(() => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        });
      }
      await window.__pdfjsLoadPromise;
      return window.pdfjsLib;
    },

    async collectPageSizes() {
      this.renderedPages = [];
      const totalPages = this.pdfDoc.numPages;
      const scale = Math.min(1.5, (window.innerWidth * 0.7) / 612);
      for (let i = 1; i <= totalPages; i++) {
        const page = await this.pdfDoc.getPage(i);
        const viewport = page.getViewport({ scale });
        this.renderedPages.push({
          width: Math.floor(viewport.width),
          height: Math.floor(viewport.height),
          page,
          viewport,
        });
      }
    },

    async paintCanvases() {
      // Cancel any previous renders still in flight so we never call render()
      // twice on the same canvas.
      this._cancelRenderTasks();
      const tasks = [];
      for (let i = 0; i < this.renderedPages.length; i++) {
        const { page, viewport } = this.renderedPages[i];
        const canvasArr = this.$refs['canvas_' + i];
        const canvas = Array.isArray(canvasArr) ? canvasArr[0] : canvasArr;
        if (!canvas) continue;
        const ctx = canvas.getContext('2d');
        const task = page.render({ canvasContext: ctx, viewport });
        tasks.push(task);
      }
      this._renderTasks = tasks;
      // Wait for all renders, swallowing cancellation errors so they don't bubble.
      await Promise.all(tasks.map(t => t.promise.catch(err => {
        if (err && err.name !== 'RenderingCancelledException') throw err;
      })));
    },

    onPageClick(e, pageIndex) {
      if (!this.selectedFieldType) {
        // Deselect any selected field when clicking blank canvas
        this.selectedFieldId = null;
        return;
      }
      const overlay = e.currentTarget;
      const rect = overlay.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      this.addField(pageIndex, x, y);
    },

    isTypeAlreadyUsed(type) {
      if (! UNIQUE_FIELD_TYPES.has(type)) return false;
      return this.fields.some(f => f.type === type && f.executing_party === this.party);
    },

    addField(pageIndex, x, y) {
      const type = this.selectedFieldType;
      if (!type) return;
      if (this.isTypeAlreadyUsed(type)) {
        this.$emit('duplicate-field', type);
        return;
      }
      const dims = FIELD_DIMS[type] || { width: 0.2, height: 0.04 };
      const field = {
        id: `f_${++fieldCounter}_${Date.now()}`,
        type,
        executing_party: this.party,
        page: pageIndex + 1,
        x_pct: Math.max(0, Math.min(1 - dims.width, x - dims.width / 2)),
        y_pct: Math.max(0, Math.min(1 - dims.height, y - dims.height / 2)),
        width_pct: dims.width,
        height_pct: dims.height,
      };
      this.fields.push(field);
      this.selectedFieldId = field.id;
      this.emitUpdate();
      this.$emit('field-placed', field);
    },

    removeField(id) {
      this.fields = this.fields.filter(f => f.id !== id);
      if (this.selectedFieldId === id) this.selectedFieldId = null;
      this.emitUpdate();
    },

    clearAllFields() {
      this.fields = this.fields.filter(f => f.executing_party !== this.party);
      this.emitUpdate();
    },

    fieldsOnPage(pageIndex) {
      return this.fields.filter(f => f.page === pageIndex + 1);
    },

    fieldStyle(field, page) {
      return {
        left: (field.x_pct * page.width) + 'px',
        top: (field.y_pct * page.height) + 'px',
        width: (field.width_pct * page.width) + 'px',
        height: (field.height_pct * page.height) + 'px',
      };
    },

    fieldLabel(field) {
      if (field.type === 'text' && field.custom_label) return field.custom_label;
      const labels = {
        signature: 'Signature Field',
        initials: 'Initials Field',
        date: 'Date Field',
        text: 'Text Field',
      };
      return labels[field.type] || field.type;
    },

    onFieldDblClick(field) {
      if (this.mode === 'sign' || this.mode === 'view') return;
      if (field.type !== 'text') return;
      if (field.executing_party !== this.party) return;
      this.editingLabelId = field.id;
      this.editingLabelValue = field.custom_label || 'Text Field';
      this.$nextTick(() => {
        const refKey = 'labelInput_' + field.id;
        const el = this.$refs[refKey];
        const input = Array.isArray(el) ? el[0] : el;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },

    saveLabelEdit(field) {
      if (this.editingLabelId !== field.id) return;
      const trimmed = this.editingLabelValue.trim();
      // Block saving a duplicate custom label among own-party text fields
      if (field.type === 'text' && trimmed) {
        const duplicate = this.fields.some(f =>
          f.id !== field.id &&
          f.type === 'text' &&
          f.executing_party === this.party &&
          f.custom_label === trimmed
        );
        if (duplicate) {
          this.$emit('duplicate-label', trimmed);
          this.editingLabelId = null;
          this.editingLabelValue = '';
          return;
        }
      }
      field.custom_label = trimmed || null;
      this.editingLabelId = null;
      this.editingLabelValue = '';
      this.emitUpdate();
    },

    cancelLabelEdit() {
      this.editingLabelId = null;
      this.editingLabelValue = '';
    },

    isSignableField(field) {
      return this.signableParties.includes(field.executing_party) && (field.type === 'signature' || field.type === 'initials');
    },

    isFillableField(field) {
      return this.signableParties.includes(field.executing_party) && !['signature', 'initials'].includes(field.type);
    },

    onFieldMouseDown(e, field, idx) {
      if (this.mode === 'sign' || this.mode === 'view') return;
      if (this.editingLabelId === field.id) return;
      this.selectedFieldId = field.id;
      this.startDrag(e, field, idx);
    },

    onFieldClick(field) {
      if (this.mode === 'sign' || this.mode === 'view') return;
      this.selectedFieldId = field.id;
    },

    selectedFieldOnPage(pageIndex) {
      if (! this.selectedFieldId) return null;
      const f = this.fields.find(f => f.id === this.selectedFieldId && f.page === pageIndex + 1);
      if (! f) return null;
      if (f.executing_party && f.executing_party !== this.party) return null;
      return f;
    },

    typeSwitcherStyle(field, page) {
      const left = (field.x_pct + field.width_pct) * page.width + 12;
      const top  = field.y_pct * page.height;
      return { left: left + 'px', top: top + 'px' };
    },

    changeSelectedFieldType(type) {
      const f = this.fields.find(ff => ff.id === this.selectedFieldId);
      if (!f || f.type === type) return;
      // Unique types: only allowed once per party in this editor
      if (UNIQUE_FIELD_TYPES.has(type)) {
        const alreadyUsed = this.fields.some(ff => ff.type === type && ff.executing_party === this.party && ff.id !== f.id);
        if (alreadyUsed) {
          this.$emit('duplicate-field', type);
          return;
        }
      }
      const dims = FIELD_DIMS[type] || { width: 0.2, height: 0.04 };
      f.type = type;
      f.width_pct = dims.width;
      f.height_pct = dims.height;
      // Keep within page bounds
      f.x_pct = Math.max(0, Math.min(1 - f.width_pct, f.x_pct));
      f.y_pct = Math.max(0, Math.min(1 - f.height_pct, f.y_pct));
      this.emitUpdate();
    },

    typeIconSvg(type) {
      const icons = {
        signature: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M3 17c2-4 4-7 6-7 1.2 0 1.8 1.6 3 1.6 1.4 0 3-2.6 5-4.6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14 8.6l-2.3 2.3M11.7 8.6L14 10.9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          <line x1="3" y1="20" x2="21" y2="20" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" opacity="0.55"/>
        </svg>`,
        initials: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M5 17V7m0 0l5 10m0-10v10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14.5 17V7m0 0l4 10m0-10v10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          <line x1="3" y1="20.5" x2="21" y2="20.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" opacity="0.55"/>
        </svg>`,
        date: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="3.25" y="5.25" width="17.5" height="15" rx="2.5" stroke="currentColor" stroke-width="1.7"/>
          <path d="M3.25 10h17.5" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 3.5v3M16 3.5v3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
          <rect x="6.5"  y="12.5" width="2.4" height="2.4" rx="0.6" fill="currentColor"/>
          <rect x="10.8" y="12.5" width="2.4" height="2.4" rx="0.6" fill="currentColor"/>
          <rect x="15.1" y="12.5" width="2.4" height="2.4" rx="0.6" fill="currentColor"/>
        </svg>`,
        text: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" stroke="currentColor" stroke-width="1.7"/>
          <path d="M8 8.5h8M12 8.5v8M9.5 16.5h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`,
      };
      return icons[type] || '';
    },

    onResizeMouseDown(e, field, pageIndex, corner) {
      if (field.executing_party !== this.party) return;
      e.preventDefault();
      this.resizeState = {
        id: field.id,
        pageIndex,
        corner,
        startMX: e.clientX,
        startMY: e.clientY,
        startW: field.width_pct,
        startH: field.height_pct,
        startX: field.x_pct,
        startY: field.y_pct,
      };
    },

    startDrag(e, field, pageIndex) {
      if (field.executing_party !== this.party) return; // don't drag cross-party fields
      this.dragState = {
        id: field.id,
        pageIndex,
        startMX: e.clientX,
        startMY: e.clientY,
        startFX: field.x_pct,
        startFY: field.y_pct,
      };
    },

    onMouseMove(e) {
      if (this.resizeState) {
        const { id, pageIndex, corner, startMX, startMY, startW, startH, startX, startY } = this.resizeState;
        const page = this.renderedPages[pageIndex];
        if (!page) return;
        const dx = (e.clientX - startMX) / page.width;
        const dy = (e.clientY - startMY) / page.height;
        const field = this.fields.find(f => f.id === id);
        if (!field) return;

        const dims = FIELD_MIN_DIMS[field.type] || { width: 0.05, height: 0.015 };
        const minW = dims.width;
        const minH = dims.height;
        let newW, newH, newX, newY;

        if (corner === 'br') {
          // Bottom-right: anchor top-left, grow right/down
          newW = Math.max(minW, Math.min(1 - startX, startW + dx));
          newH = Math.max(minH, Math.min(1 - startY, startH + dy));
          newX = startX;
          newY = startY;
        } else if (corner === 'bl') {
          // Bottom-left: anchor top-right, grow left/down
          newW = Math.max(minW, Math.min(startX + startW, startW - dx));
          newH = Math.max(minH, Math.min(1 - startY, startH + dy));
          newX = startX + startW - newW;
          newY = startY;
        } else if (corner === 'tr') {
          // Top-right: anchor bottom-left, grow right/up
          newW = Math.max(minW, Math.min(1 - startX, startW + dx));
          newH = Math.max(minH, Math.min(startY + startH, startH - dy));
          newX = startX;
          newY = startY + startH - newH;
        } else {
          // Top-left: anchor bottom-right, grow left/up
          newW = Math.max(minW, Math.min(startX + startW, startW - dx));
          newH = Math.max(minH, Math.min(startY + startH, startH - dy));
          newX = startX + startW - newW;
          newY = startY + startH - newH;
        }

        field.width_pct  = newW;
        field.height_pct = newH;
        field.x_pct = Math.max(0, newX);
        field.y_pct = Math.max(0, newY);

        // Sync all initials fields for the same party
        if (field.type === 'initials') {
          this.fields.forEach(f => {
            if (f.type === 'initials' && f.executing_party === field.executing_party && f.id !== id) {
              // Determine each field's anchor edges before resizing
              const fRight  = f.x_pct + f.width_pct;
              const fBottom = f.y_pct + f.height_pct;
              f.width_pct  = newW;
              f.height_pct = newH;
              // Keep right-anchored fields pinned to their right edge
              if (fRight > 0.5) {
                f.x_pct = Math.max(0, fRight - newW);
              }
              // Keep bottom-anchored fields pinned to their bottom edge
              if (fBottom > 0.5) {
                f.y_pct = Math.max(0, fBottom - newH);
              }
              // Clamp so field doesn't overflow the page
              if (f.x_pct + f.width_pct > 1) {
                f.x_pct = Math.max(0, 1 - f.width_pct);
              }
              if (f.y_pct + f.height_pct > 1) {
                f.y_pct = Math.max(0, 1 - f.height_pct);
              }
            }
          });
        }
        return;
      }
      if (!this.dragState) return;
      const { id, pageIndex, startMX, startMY, startFX, startFY } = this.dragState;
      const page = this.renderedPages[pageIndex];
      if (!page) return;
      const dx = (e.clientX - startMX) / page.width;
      const dy = (e.clientY - startMY) / page.height;
      const field = this.fields.find(f => f.id === id);
      if (!field) return;
      field.x_pct = Math.max(0, Math.min(1 - field.width_pct, startFX + dx));
      field.y_pct = Math.max(0, Math.min(1 - field.height_pct, startFY + dy));
    },

    onMouseUp() {
      if (this.resizeState) {
        this.resizeState = null;
        this.emitUpdate();
        return;
      }
      if (this.dragState) {
        this.dragState = null;
        this.emitUpdate();
      }
    },

    emitUpdate() {
      const ownFields = this.fields.filter(f => f.executing_party === this.party);
      this.$emit('fields-updated', ownFields);
    },

    // Called by parent to get current fields
    getFields() {
      return this.fields.filter(f => f.executing_party === this.party);
    },

    // Preload own-party fields from an existing saved contract so the editor
    // can display them and the uniqueness check can see them.
    preloadOwnFields(ownFields) {
      const crossParty = this.fields.filter(f => f.executing_party !== this.party);
      this.fields = [...crossParty, ...ownFields.map(f => ({ ...f }))];
      // Don't emit — parent already holds these fields as the source of truth
    },

    clearSelection() {
      this.selectedFieldId = null;
    },

    getPdfFile() {
      return this.pdfFile;
    },

    getPdfBytes() {
      return this.pdfBytes;
    },

    // Place an initials field at the given corner on every page
    placeInitialsOnAllPages(position) {
      const dims = FIELD_DIMS.initials;
      const margin = 0.02;
      let x_pct, y_pct;
      switch (position) {
        case 'top-left':     x_pct = margin;                      y_pct = margin; break;
        case 'top-right':    x_pct = 1 - dims.width - margin;    y_pct = margin; break;
        case 'bottom-left':  x_pct = margin;                      y_pct = 1 - dims.height - margin; break;
        case 'bottom-right': x_pct = 1 - dims.width - margin;    y_pct = 1 - dims.height - margin; break;
        default: return;
      }
      // Remove any existing own-party initials before placing the new set
      this.fields = this.fields.filter(f => !(f.type === 'initials' && f.executing_party === this.party));
      for (let i = 0; i < this.renderedPages.length; i++) {
        this.fields.push({
          id: `f_${++fieldCounter}_${Date.now()}_p${i}`,
          type: 'initials',
          executing_party: this.party,
          page: i + 1,
          x_pct,
          y_pct,
          width_pct: dims.width,
          height_pct: dims.height,
        });
      }
      this.emitUpdate();
      this.$emit('field-placed');
    },
  },
};
</script>

<style scoped>
.pdf-field-editor {
  width: 100%;
}

/* ── Loading state ───────────────────────────────────────────────── */
.pdf-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  color: #9ca3af;
  font-size: 14px;
  gap: 12px;
}
.pdf-loading-state .fa-spinner {
  font-size: 28px;
}

/* ── Field placement hint bar ────────────────────────────────────── */
.pdf-place-hint {
  text-align: center;
  padding: 8px 16px;
  background: #ede9fe;
  color: #5b21b6;
  font-size: 13px;
  border-bottom: 1px solid #ddd6fe;
  margin-bottom: 0;
}

.pdf-viewer-container {
  width: 100%;
}

/* Hide canvases while they are being painted (avoid blank-canvas flicker) */
.pdf-viewer--painting {
  visibility: hidden;
}

.pdf-page-wrap {
  position: relative;
  display: block;
  margin: 0 auto 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  background: #fff;
}

.pdf-page-canvas {
  display: block;
  pointer-events: none;
}

.pdf-page-overlay {
  position: absolute;
  top: 0;
  left: 0;
  cursor: default;
}

.pdf-page-overlay--active {
  cursor: crosshair;
}

.pdf-field {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  border-radius: 0;
  cursor: grab;
  user-select: none;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid transparent;
  z-index: 10;
  box-sizing: border-box;
  gap: 6px;
}

.pdf-field--dragging {
  opacity: 0.8;
  cursor: grabbing;
  z-index: 20;
}

/* Selected (focused) field — outline + soft spread shadow.
   Per-party tints override .pdf-field--selected colours below. */
.pdf-field--selected {
  z-index: 25;
}

/* Type switcher popover */
.pdf-type-switcher {
  position: absolute;
  background: #fff;
  border-radius: 0;
  box-shadow: 0 14px 40px rgba(20, 20, 40, 0.18), 0 4px 14px rgba(20, 20, 40, 0.10);
  min-width: 240px;
  padding: 8px;
  z-index: 50;
  user-select: none;
  display: flex;
  flex-direction: column;
  gap: 0;
}
/* Arrow pointing left toward the selected field */
.pdf-type-switcher::before,
.pdf-type-switcher::after {
  content: '';
  position: absolute;
  top: 28px;
  width: 0;
  height: 0;
  border-style: solid;
}
.pdf-type-switcher::before {
  left: -10px;
  border-width: 10px 10px 10px 0;
  border-color: transparent rgba(20, 20, 40, 0.08) transparent transparent;
  filter: blur(0.5px);
}
.pdf-type-switcher::after {
  left: -8px;
  border-width: 9px 9px 9px 0;
  border-color: transparent #ffffff transparent transparent;
}
.pdf-type-switcher-item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  font-size: 16px;
  color: #1a1a2e;
  background: none;
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: background 0.15s;
  font-weight: 500;
  position: relative;
}
.pdf-type-switcher-item + .pdf-type-switcher-item {
  margin-top: 0;
}
.pdf-type-switcher-item:hover {
  background: #faf3f5;
}
.pdf-type-switcher-item--active {
  background: #fbecef;
  color: #1a1a2e;
  font-weight: 600;
}
.pdf-type-switcher-sep {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: -1px;
  height: 1px;
  background: #ececec;
}
/* Hide separator under / over the active row to keep its pill clean */
.pdf-type-switcher-item--active .pdf-type-switcher-sep,
.pdf-type-switcher-item--active + .pdf-type-switcher-item .pdf-type-switcher-sep {
  display: none;
}
.pdf-type-switcher-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f1ecff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6d28d9;
  flex-shrink: 0;
}
.pdf-type-switcher-item:hover .pdf-type-switcher-icon {
  background: #e6dfff;
}
.pdf-type-switcher-item--active .pdf-type-switcher-icon {
  background: #ddd6fe;
  color: #4c1d95;
}
.pdf-type-switcher-item--used {
  opacity: 0.38;
  cursor: not-allowed;
}
.pdf-type-switcher-item--used:hover {
  background: transparent;
}
.pdf-type-switcher-item--used .pdf-type-switcher-icon {
  background: #f3f4f6;
  color: #9ca3af;
}
.pdf-type-switcher-label {
  flex: 1;
}

/* Venue fields in venue editor — light gray fill, dark gray border */
.pdf-field--party-venue {
  background: #f3f4f6;
  color: #1a1a2e;
  border-color: #4b5563;
}
.pdf-field--party-venue.pdf-field--selected {
  border-color: #4b5563 !important;
  box-shadow: 0 0 18px 4px rgba(75, 85, 99, 0.30);
}

/* Venue fields shown inside the client editor — muted (inactive) */
.pdf-field-editor--client .pdf-field--party-venue {
  background: #f3f4f6;
  color: #9ca3af;
  border-color: #d1d5db;
  border-style: dashed;
  cursor: not-allowed;
}

/* VenueCrew fields — light teal fill, teal border */
.pdf-field--party-venuecrew {
  background: #e0f2fe;
  color: #0c4a6e;
  border-color: #0284c7;
}
.pdf-field--party-venuecrew.pdf-field--selected {
  border-color: #0284c7 !important;
  box-shadow: 0 0 18px 4px rgba(2, 132, 199, 0.30);
}

/* VenueCrew fields shown inside other editors — muted (inactive) */
.pdf-field-editor--client .pdf-field--party-venuecrew,
.pdf-field-editor--venue .pdf-field--party-venuecrew {
  background: #e0f2fe;
  color: #9ca3af;
  border-color: #7dd3fc;
  border-style: dashed;
  cursor: not-allowed;
}

/* Client fields — pale lavender fill with red border */
.pdf-field--party-client {
  background: #efe9ff;
  color: #1a1a2e;
  border-color: #ef4444;
}
.pdf-field--party-client.pdf-field--selected {
  border-color: #ef4444 !important;
  box-shadow: 0 0 22px 4px rgba(168, 134, 255, 0.55);
}

.pdf-field-label {
  flex: 1;
  pointer-events: none;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 600;
  color: inherit;
}

/* Text and Date fields — larger font, padding */
.pdf-field--text,
.pdf-field--date {
  font-size: 14px;
  font-weight: 600;
  padding: 5px 8px;
}
.pdf-field--text .pdf-field-label,
.pdf-field--date .pdf-field-label {
  font-size: 14px;
  font-weight: 600;
}

.pdf-field-label-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: inherit;
  width: 100%;
  padding: 0;
  min-width: 0;
  cursor: text;
}

.pdf-field-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.pdf-field-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #374151;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  transition: color 0.1s, background 0.1s;
}

.pdf-field-action-btn:hover {
  background: rgba(0,0,0,0.07);
  color: #111827;
}

.pdf-field-remove:hover {
  color: #ef4444 !important;
}

.pdf-field-drag-handle {
  cursor: grab;
}

/* Resize handles — all 4 corners */
.pdf-field-resize-handle {
  position: absolute;
  width: 9px;
  height: 9px;
  opacity: 0.55;
  flex-shrink: 0;
}
.pdf-field-resize-handle:hover {
  opacity: 1;
}
.pdf-field-resize-handle--br {
  bottom: 2px;
  right: 2px;
  cursor: se-resize;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  border-radius: 0 0 2px 0;
}
.pdf-field-resize-handle--bl {
  bottom: 2px;
  left: 2px;
  cursor: sw-resize;
  border-left: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  border-radius: 0 0 0 2px;
}
.pdf-field-resize-handle--tr {
  top: 2px;
  right: 2px;
  cursor: ne-resize;
  border-right: 2px solid currentColor;
  border-top: 2px solid currentColor;
  border-radius: 0 2px 0 0;
}
.pdf-field-resize-handle--tl {
  top: 2px;
  left: 2px;
  cursor: nw-resize;
  border-left: 2px solid currentColor;
  border-top: 2px solid currentColor;
  border-radius: 2px 0 0 0;
}

.pdf-page-number {
  position: absolute;
  bottom: 4px;
  right: 8px;
  font-size: 10px;
  color: #999;
  pointer-events: none;
}

/* ── Sign mode ───────────────────────────────────────────────────── */
.pdf-field--sign-mode {
  cursor: default !important;
}

.pdf-field--party-venue.pdf-field--sign-mode {
  background: rgba(108, 99, 255, 0.08);
  border: 1.5px solid #6c63ff;
  color: #6c63ff;
}

.pdf-field--party-client.pdf-field--sign-mode {
  background: rgba(0, 0, 0, 0.04);
  border: 1.5px dashed #aaa;
  color: #666;
}

.pdf-field-signed-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
  display: block;
}
.pdf-field-signed-img--resigable {
  pointer-events: auto;
  cursor: pointer;
}
.pdf-field-signed-img--resigable:hover {
  opacity: 0.82;
}

.pdf-field-clear-sign {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ef4444;
  color: #fff;
  border: none;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 30;
  padding: 0;
  line-height: 1;
}
.pdf-field-clear-sign:hover {
  background: #dc2626;
}

.pdf-field-sign-btn,
.pdf-field-fill-btn {
  background: transparent;
  border: none;
  color: #6c63ff;
  font-size: 11px;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.pdf-field-sign-btn:hover,
.pdf-field-fill-btn:hover {
  color: #4f46e5;
}

/* Fillable text field in sign mode — stack placeholder above hint */
.pdf-field-fill-btn {
  flex-direction: column;
  gap: 1px;
}

.pdf-field-fill-placeholder {
  font-size: 12px;
  font-weight: 700;
  color: #1a1a4e;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
  white-space: nowrap;
  pointer-events: none;
}

.pdf-field-fill-hint {
  font-size: 10px;
  font-weight: 500;
  color: #6c63ff;
  opacity: 0.85;
  pointer-events: none;
}

.pdf-field-filled-value {
  font-size: 11px;
  font-weight: 700;
  color: #1a1a4e;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
  pointer-events: none;
  white-space: nowrap;
}
.pdf-field-filled-value--readonly {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  cursor: default;
}
</style>
